<template>
    <el-form @submit.native.prevent class="form-customize-calendar">
        <el-row :gutter="20">
            <el-col :span="12">
                <div class="customize-card">
                    <h3 class="card-title">Location Keys / Custom Text</h3>
                    <p>
                        If space is available on your calendar, you may add a
                        Location Key and/or Custom Text here. Once you are done,
                        proofread them carefully. The information from the
                        previous calendar will be listed here.
                    </p>
                    <el-form
                        @submit.native.prevent
                        class="location-keys"
                        label-width="100px"
                    >
                        <table style="width: 100%">
                            <thead>
                                <tr>
                                    <th style="width: 27px" />
                                    <th style="width: 120px">Abbreviations</th>
                                    <th>Spelled Out</th>
                                    <th>Color</th>
                                    <th style="width: 50px" />
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    :key="i"
                                    v-for="(
                                        location_key, i
                                    ) in form.location_keys"
                                >
                                    <td
                                        class="pt-15"
                                        style="vertical-align: top"
                                    >
                                        {{ i + 1 }}.
                                    </td>
                                    <td style="vertical-align: top">
                                        <el-form-item
                                            :class="
                                                form.state(
                                                    `location_keys.${i}.key`
                                                )
                                            "
                                            class="mb-0"
                                            label-width="0"
                                            required
                                        >
                                            <el-input
                                                :maxlength="
                                                    (location_key.key
                                                        ? location_key.key
                                                              .length
                                                        : 0) +
                                                    availableLocationKeysAndCustomTextCharCount
                                                "
                                                v-model="location_key.key"
                                            />
                                            <form-feedback
                                                :field="`location_keys.${i}.key`"
                                                :form="form"
                                                :formatter="formatErrorMessage"
                                            />
                                        </el-form-item>
                                    </td>
                                    <td style="vertical-align: top">
                                        <el-form-item
                                            :class="
                                                form.state(
                                                    `location_keys.${i}.value`
                                                )
                                            "
                                            class="mb-0"
                                            label-width="0"
                                            required
                                        >
                                            <el-input
                                                :maxlength="
                                                    (location_key.value
                                                        ? location_key.value
                                                              .length
                                                        : 0) +
                                                    availableLocationKeysAndCustomTextCharCount
                                                "
                                                @input="
                                                    onChangeLocationKeyValue(
                                                        location_key
                                                    )
                                                "
                                                v-model="location_key.value"
                                            />
                                            <form-feedback
                                                :field="`location_keys.${i}.value`"
                                                :form="form"
                                                :formatter="formatErrorMessage"
                                            />
                                        </el-form-item>
                                    </td>
                                    <td style="vertical-align: top">
                                        <el-form-item
                                            :class="
                                                form.state(
                                                    `location_keys.${i}.color`
                                                )
                                            "
                                            class="mb-0"
                                            label-width="0"
                                            required
                                        >
                                            <color-picker
                                                v-model="location_key.color"
                                                :predefine="predefinedColors"
                                            >
                                            </color-picker>
                                        </el-form-item>
                                    </td>
                                    <td
                                        class="pt-10"
                                        style="vertical-align: top"
                                    >
                                        <el-button
                                            type="danger"
                                            icon="mdi mdi-delete"
                                            size="mini"
                                            @click="
                                                deleteLocationKey(location_key)
                                            "
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td />
                                    <td colspan="3">
                                        <el-button
                                            type="primary"
                                            round
                                            size="mini"
                                            icon="mdi mdi-plus"
                                            :disabled="hasMaxedOutLines"
                                            @click="newLocationKey()"
                                            >Add Line
                                        </el-button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <el-input
                            class="mt-10"
                            type="textarea"
                            :maxlength="
                                (form.custom_text
                                    ? form.custom_text.length
                                    : 0) +
                                availableLocationKeysAndCustomTextCharCount
                            "
                            :rows="8"
                            v-model="form.custom_text"
                        >
                        </el-input>
                        <p class="mt-0 mb-5">
                            {{ locationKeysAndCustomTextCharCount }} /
                            {{ customTextMaxChars }}
                            <el-divider direction="vertical" />
                            Lines: {{ customTextLines }} /
                            {{ customTextMaxLines }}
                        </p>
                        <el-button
                            class="mb-10"
                            type="danger"
                            @click="form.custom_text = null"
                            >Delete Text</el-button
                        >
                    </el-form>
                    <el-form-item>
                        <el-button
                            type="primary"
                            icon="mdi mdi-pencil"
                            @click="showTitleDialog()"
                            >Change Title on Calendar
                        </el-button>
                    </el-form-item>
                </div>
            </el-col>
            <el-col :span="12">
                <div class="customize-card">
                    <h3 class="card-title">Add Logo</h3>
                    <p>
                        Use this to upload your facility logo. Your corporation
                        may have a logo uploaded for you in this space.
                    </p>
                    <el-form-item class="mb-0">
                        <input
                            @change="onChangeLogo"
                            accept="image/jpeg,image/png,application/pdf"
                            placeholder="Select logo"
                            type="file"
                        />
                    </el-form-item>
                    <p class="italic mt-0 text-small">
                        Format and size:<br />
                        You can upload a JPG, PNG, PDF or EPS image file. The
                        logo should be at least 1200 x 800 pixels.
                    </p>
                    <div v-if="logo" class="logo-preview">
                        <img
                            v-if="logo"
                            :src="logo"
                            style="max-width: 300px"
                        /><br />
                        <el-button
                            @click="removeLogo()"
                            type="danger"
                            icon="mdi mdi-close"
                            class="mt-10"
                            >Remove Logo</el-button
                        >
                        <span v-if="!form.logo" class="text-small"
                            >(Default Logo)</span
                        >
                    </div>
                </div>
            </el-col>
        </el-row>
        <el-dialog
            :close-on-click-modal="false"
            title="Update Calendar Title"
            :visible.sync="titleDialogVisible"
            width="450px"
        >
            <el-form @submit.native.prevent>
                <p>
                    Update the Title of your calendar here. The default format
                    is “Facility Name • Calendar Name”
                </p>
                <el-form-item :class="titleForm.state('title')">
                    <el-input
                        @click.native="onClickTitle($event, titleForm)"
                        @focus.native="onFocusTitle($event, titleForm)"
                        @keyup.native="onKeyUpTitle($event, titleForm)"
                        @keypress.enter.native="
                            onEnterEventTitle($event, titleForm)
                        "
                        v-model="titleForm.title"
                    />
                    <form-feedback :form="titleForm" field="title" />
                </el-form-item>
            </el-form>
            <div slot="footer" class="dialog-footer">
                <el-button @click="titleDialogVisible = false"
                    >Cancel</el-button
                >
                <el-button type="primary" @click="saveTitle()">OK</el-button>
            </div>
        </el-dialog>
    </el-form>
</template>

<script>
import * as BusEvents from "@/constants/bus-events";
import FileUtils from "@/utils/file";

export default {
    props: {
        form: {
            type: Object,
            required: true
        },
        calendar: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            logo: null,
            titleDialogVisible: false,
            titleForm: new Form({
                title: null
            }),
            titleInputCursor: {
                start: 0,
                end: 0
            }
        };
    },
    mounted() {
        Vue.nextTick(() => {
            this.form.setVeeErrors(this.veeErrors);
        });
        if (!this.form.remove_logo) {
            if (this.form.logo_src) {
                this.logo = this.form.logo_src;
            }
        }
        if (!this.form.location_keys.length) {
            this.newLocationKey();
        }
    },
    computed: {
        current_community() {
            return this.$parent.current_community;
        },
        locationKeysAndCustomTextCharCount() {
            let length = 0;
            this.form.location_keys.forEach((item) => {
                length +=
                    (item.key ? item.key.length : 0) +
                    (item.value ? item.value.length : 0);
            });
            if (this.form.custom_text) {
                length += this.form.custom_text.length;
            }
            return length;
        },
        availableLocationKeysAndCustomTextCharCount() {
            return (
                this.customTextMaxChars -
                this.locationKeysAndCustomTextCharCount
            );
        },
        customTextMaxChars() {
            return this.$store.state.config.settings.calendar
                .custom_text_max_length;
        },
        customTextMaxLines() {
            return this.calendarCalculator.custom_text_max_lines;
        },
        customTextLines() {
            let lines = _.sumBy(this.form.location_keys, (lk) =>
                lk.value_lines.length
                    ? lk.value_lines.length
                    : Utils.emptyOrNull(lk.key)
                    ? 0
                    : 1
            );
            return (
                lines +
                this.calendarCalculator.getCustomTextLines(
                    this.form.custom_text
                ).length
            );
        },
        hasMaxedOutLines() {
            return this.customTextLines >= this.customTextMaxLines;
        },
        hasExceededMaxLines() {
            return this.customTextLines > this.customTextMaxLines;
        },
        hasValidationErrors() {
            return this.veeErrors.any();
        },
        /**
         * @returns {CalendarCalculator}
         */
        calendarCalculator() {
            return this.$parent.calendarCalculator;
        },
        predefinedColors() {
            return this.$store.state.config.settings.font.colors;
        }
    },
    watch: {
        "form.custom_text"(value, old) {
            let old_lines =
                this.calendarCalculator.getCustomTextLines(old).length +
                this.form.location_keys.length;
            if (
                this.customTextLines > this.customTextMaxLines &&
                old_lines <= this.customTextMaxLines
            ) {
                this.form.custom_text = old;
            }
        }
    },
    methods: {
        onChangeLogo(e) {
            let file = _.get(e, "target.files.0");
            if (!file) {
                this.logo = null;
                this.form.logo = null;
                return;
            }
            FileUtils.checkFileSize(file).then(() => {
                if (FileUtils.isPdf(file)) {
                    this.form.logo = file;
                    this.form.remove_logo = false;
                    this.logo = "/images/pdf-icon.svg";
                    Vue.nextTick(() => {
                        window.Bus.$emit(BusEvents.UPDATE_SCROLLER);
                    });
                } else {
                    window.Utils.getFileImageData(e.target).then((response) => {
                        let img = new Image();
                        img.onload = () => {
                            if (
                                img.naturalWidth < 1200 ||
                                img.naturalHeight < 800
                            ) {
                                this.$alert(
                                    "Your logo resolution is below the minimum requirement (1200 x 800).",
                                    "Oops!",
                                    {
                                        confirmButtonText: "OK",
                                        type: "warning"
                                    }
                                );
                            } else {
                                this.logo = response;
                                this.form.logo = file;
                                this.form.remove_logo = false;
                                Vue.nextTick(() => {
                                    window.Bus.$emit(BusEvents.UPDATE_SCROLLER);
                                });
                            }
                        };
                        img.src = response;
                    });
                }
            });
        },
        removeLogo() {
            this.logo = null;
            this.form.logo = null;
            this.form.remove_logo = true;
        },
        showTitleDialog() {
            this.titleDialogVisible = true;
            let title = this.form.title
                ? this.form.title
                : this.calendar.default_title;
            this.titleForm.title = title
                ? title.replaceAll(
                      "\n",
                      App.state.config.settings.calendar.newline_char
                  )
                : null;
        },
        saveTitle() {
            this.titleDialogVisible = false;
            this.form.update_title = true;
            this.form.title = this.titleForm.title
                ? this.titleForm.title.replaceAll(
                      App.state.config.settings.calendar.newline_char,
                      "\n"
                  )
                : null;
        },
        deleteLocationKey(key) {
            Utils.arrayDelete(this.form.location_keys, key);
        },
        newLocationKey() {
            this.form.location_keys.push({
                key: null,
                value: null,
                color: "#000",
                value_lines: []
            });
            this.veeErrors.update();
            Vue.nextTick(() => {
                this.$validator.validate();
            });
        },
        onChangeLocationKeyValue(location_key) {
            // Debounce the value_lines update
            clearTimeout(this._valueUpdateTimeout);
            this._valueUpdateTimeout = setTimeout(() => {
                if (location_key.value && location_key.value.trim()) {
                    location_key.value_lines =
                        this.calendarCalculator.getLocationKeyValueLines(
                            location_key
                        );
                    // Track typing activity for auto-save debouncing
                    if (
                        this.$parent &&
                        typeof this.$parent.trackTypingActivity === "function"
                    ) {
                        this.$parent.trackTypingActivity();
                    }
                } else {
                    location_key.value_lines = [];
                }
            }, 300); // 300ms delay to wait for typing to pause
        },
        updateLocationKeyValueLines() {
            this.form.location_keys.forEach((item) => {
                item.value_lines =
                    this.calendarCalculator.getLocationKeyValueLines(item);
            });
        },
        onEnterEventTitle(e, titleForm) {
            let title = titleForm.title ? titleForm.title : "";
            let start = title.slice(0, this.titleInputCursor.start);
            let end = title.slice(this.titleInputCursor.end, title.length);
            titleForm.title =
                start + App.state.config.settings.calendar.newline_char + end;
            Vue.nextTick(() => {
                e.target.setSelectionRange(
                    this.titleInputCursor.start + 1,
                    this.titleInputCursor.start + 1
                );
            });
        },
        saveTitleCursor(target) {
            this.titleInputCursor.start = target.selectionStart;
            this.titleInputCursor.end = target.selectionEnd;
        },
        onKeyUpTitle(e, event) {
            this.saveTitleCursor(e.target);
        },
        onFocusTitle(e, event) {
            this.saveTitleCursor(e.target);
        },
        onClickTitle(e, event) {
            this.saveTitleCursor(e.target);
        },
        formatErrorMessage(error) {
            if (
                error.includes("location_keys.") &&
                error.includes("value_lines")
            ) {
                // Extract the location key index
                const match = error.match(/location_keys\.(\d+)/);
                const index = match ? parseInt(match[1]) : null;

                if (index !== null && index < this.form.location_keys.length) {
                    // Get the abbreviation key name for better context
                    const keyName =
                        this.form.location_keys[index].key ||
                        `Item #${index + 1}`;
                    return `Please complete the text for "${keyName}". Your text may be too short or incomplete.`;
                }
                return "Please complete all location key values before saving.";
            }
            return error;
        }
    }
};
</script>

<style lang="scss" scoped>
@use "../../../../../assets/scss/variables" as vars;

.form-customize-calendar {
    .el-form-item {
        max-width: 600px;
    }
    padding-top: 20px;
}

.location-keys {
    width: 100%;
}

.customize-card {
    background-color: #f9f9f9;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    padding: 20px;
    border: 1px solid #eaeaea;
    margin-bottom: 20px;
    height: 100%;
}

.card-title {
    margin-bottom: 20px;
    padding-left: 5px;
    color: #333;
    font-size: 18px;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 10px;
}

.logo-preview {
    margin-top: 15px;
    padding: 15px;
    border-radius: 4px;
    background-color: #fff;
    border: 1px solid #f0f0f0;
    text-align: center;
}

table {
    width: 100%;
    
    th {
        text-align: left;
        padding: 8px 5px;
        font-weight: bold;
    }
    
    td {
        padding: 5px;
    }
}

/* Media query for responsiveness */
@media (max-width: 768px) {
    .customize-card {
        margin-bottom: 20px;
    }
}

/* Override Element UI textarea to better fit the card */
:deep(.el-textarea__inner) {
    background-color: #fff;
    border: 1px solid #dcdfe6;
}

/* Improved button styling */
:deep(.el-button) {
    &.el-button--primary {
        background-color: vars.$corwin-blue-green;
        border-color: vars.$corwin-blue-green;
    }
    
    &.el-button--danger {
        background-color: vars.$text-color-danger;
        border-color: vars.$text-color-danger;
    }
}
</style>
